import { useState } from "react";
import { useTranslation } from "react-i18next";

const TaxCalculationExamples = ({ examples = [], onSelectExample }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  if (!examples || examples.length === 0) {
    return null;
  }

  const handleSelectExample = (exampleData, index) => {
    setSelectedIndex(index);
    if (onSelectExample) {
      onSelectExample(exampleData);
    }
  };

  return (
    <div className="bg-white rounded-card border border-slate-200/60 shadow-sm overflow-hidden">
      {/* Header - Collapsible Toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 sm:px-6 py-4 flex items-center justify-between transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        aria-expanded={isOpen}
      >
        <h3 className="text-base font-semibold text-slate-900">
          {t("taxCalculator.examples.title")}
        </h3>
        <svg
          className={`w-5 h-5 text-slate-500 transition-transform duration-200 ease-out ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Content - Collapsible */}
      {isOpen && (
        <div className="px-5 sm:px-6 pb-5 space-y-3">
          <p className="text-[13px] leading-relaxed text-slate-500 mb-4">
            {t("taxCalculator.examples.description")}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {examples.map((example, index) => (
              <ExampleCard
                key={index}
                example={example}
                isSelected={selectedIndex === index}
                onSelect={(data) => handleSelectExample(data, index)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ExampleCard = ({ example, isSelected, onSelect }) => {
  const { t } = useTranslation();

  const handleClick = () => {
    if (onSelect && example.autoFillData) {
      onSelect(example.autoFillData);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`group rounded-inner border p-4 text-left transition-[box-shadow,border-color,transform] duration-150 ease-out active:scale-[0.99] motion-reduce:transform-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
        isSelected
          ? "bg-white border-blue-400 ring-1 ring-blue-200"
          : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm"
      }`}
    >
      <div className="space-y-2">
        {/* Title and Object Type */}
        <div>
          <h4
            className={`text-sm font-semibold transition-colors ${
              isSelected ? "text-blue-900" : "text-slate-900"
            }`}
          >
            {example.titleKey ? t(example.titleKey) : example.title}
          </h4>
          <p
            className={`mt-1 text-xs ${
              isSelected ? "text-blue-700" : "text-slate-500"
            }`}
          >
            {example.objectTypeKey
              ? t(example.objectTypeKey)
              : example.objectType}
          </p>
        </div>

        {/* Description */}
        <p
          className={`text-xs leading-relaxed ${
            isSelected ? "text-blue-800" : "text-slate-500"
          }`}
        >
          {example.descriptionKey
            ? t(example.descriptionKey)
            : example.description}
        </p>
      </div>
    </button>
  );
};

export default TaxCalculationExamples;
